import React, { useState, useEffect, useCallback } from 'react';
import Select, { components } from 'react-select';
import { Tooltip } from 'bootstrap';
import PageHeader from '../../components/PageHeader';
import { getDurationLabel } from '../../utils/utils';
import { debounce } from '../../utils/debounce';
import { isMobile } from 'react-device-detect';

const InstituteBatchList = () => {
  const [batchNameFilter, setBatchNameFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filterMessage, setFilterMessage] = useState({ type: '', text: '' });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [batches, setBatches] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0
  });

  const Institute_id = sessionStorage.getItem('instituteId');
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  const hideAllTooltips = () => {
    try {
      const els = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      els.forEach(el => {
        const inst = Tooltip.getInstance(el);
        if (inst) inst.hide();
      });
      document.querySelectorAll('.tooltip').forEach(node => node.remove());
    } catch {}
  };

  // Fetch programs for filter dropdown
  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(
        `${LMS}/api/institutes/${Institute_id}/programs`,
        {
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      const result = await response.json();
      if (result.status) {
        setPrograms(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchBatches = async (page = 1, filters = {}) => {
    setIsLoading(true);
    try {
      const payload = {
        institute_id: parseInt(Institute_id)
      };

      if (filters.batchName) payload.batch_name = filters.batchName;
      if (filters.program) payload.program_name = filters.program;
      if (filters.status) payload.status = filters.status.toLowerCase();

      const response = await fetch(
        `${LMS}/api/batch/getlist?page=${page}`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();
      
      if (result.status && result.data) {
        const batchData = result.data.data || [];
        setBatches(batchData);
        
        setPagination({
          current_page: result.data.current_page,
          last_page: result.data.last_page,
          per_page: result.data.per_page,
          total: result.data.total,
          from: result.data.from,
          to: result.data.to
        });

        if (batchData.length === 0 && (filters.batchName || filters.program || filters.status)) {
          setFilterMessage({ 
            type: 'warning', 
            text: 'No batches found matching your filter criteria.' 
          });
        } else if (batchData.length > 0) {
          setFilterMessage({ 
            type: 'info', 
            text: `Showing ${result.data.from} to ${result.data.to} of ${result.data.total} batches.` 
          });
        } else {
          setFilterMessage({ type: '', text: '' });
        }
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      setFilterMessage({ 
        type: 'danger', 
        text: 'Failed to fetch batches. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudentList = async (batchId, programId) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${LMS}/api/get-student-list`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ batch_id: batchId, program_id: parseInt(programId), institute_id: parseInt(Institute_id) })
        }
      );

      const result = await response.json();
      
      if (result.status && result.data) {
        setAvailableStudents(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching student list:', error);
      setFilterMessage({ 
        type: 'danger', 
        text: 'Failed to fetch students. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Create a stable debounced function
  const debouncedFetchBatches = useCallback(
    debounce((page, filters) => {
      fetchBatches(page, filters);
    }, 500),
    []
  );

  // Fetch batches when filters change
  useEffect(() => {
    const filters = {
      batchName: batchNameFilter,
      program: programFilter,
      status: statusFilter
    };

    // Use debounced fetch for batch name filter (text input)
    // Use immediate fetch for dropdown filters
    if (batchNameFilter) {
      debouncedFetchBatches(1, filters);
    } else {
      fetchBatches(1, filters);
    }
  }, [batchNameFilter, programFilter, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        const bootstrap = await import('bootstrap/dist/js/bootstrap.bundle.min.js');
        const triggers = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltips = [...triggers].map(
          (t) => new bootstrap.Tooltip(t, { boundary: 'window' })
        );
        return () => tooltips.forEach((t) => t.dispose());
      } catch {}
    })();
  }, [batches]);

  const handleReset = () => {
    setBatchNameFilter('');
    setProgramFilter('');
    setStatusFilter('');
    setSearchFilter('');
    setFilterMessage({ type: '', text: '' });
    fetchBatches(1, { batchName: '', program: '', status: '' });
  };

  const handleAddBatch = () => (window.location.href = '/institute-add-batch');

  const handleView = (batch) => {
    hideAllTooltips();
    setSelectedBatch(batch);
    setShowViewModal(true);
  };

  const handleEdit = (batch) => (window.location.href = `/institute-edit-batch/${batch.id}`);
  
  const handleDelete = (batch) => {
    hideAllTooltips();
    setSelectedBatch(batch);
    setShowDeleteModal(true);
  };

  const handleAddStudent = (batch) => {
    hideAllTooltips();
    setSelectedBatch(batch);
    setShowAddStudentModal(true);
    setSelectedStudents([]);
    setDropdownOpen(false);
    fetchStudentList(batch.id, batch.program_id);
  };

  const handleStudentSelect = (student) => {
    setSelectedStudents(prev => {
      const isSelected = prev.some(s => s.id === student.id);
      if (isSelected) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  const removeSelectedStudent = (studentId) => {
    setSelectedStudents(prev => prev.filter(s => s.id !== studentId));
  };

  const handleSaveStudent = async () => {
    hideAllTooltips();
    setIsLoading(true);
    try {
      const studentIds = selectedStudents.map(s => s.id);
      const response = await fetch(
        `${LMS}/api/update-batch`,
        {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            batch_id: selectedBatch.id,
            id: studentIds
          })
        }
      );

      const result = await response.json();
      
      if (result.status) {
        setFilterMessage({ 
          type: 'success', 
          text: `Successfully added ${selectedStudents.length} student(s) to ${selectedBatch.batch_name}.` 
        });
        setShowAddStudentModal(false);
        setSelectedStudents([]);
        setDropdownOpen(false);
        setSelectedBatch(null);
        fetchBatches(pagination.current_page);
      } else {
        setFilterMessage({ 
          type: 'danger', 
          text: 'Failed to add students. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error adding students:', error);
      setFilterMessage({ 
        type: 'danger', 
        text: 'Failed to add students. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showViewModal || showAddStudentModal || showDeleteModal) {
      hideAllTooltips();
    }
  }, [showViewModal, showAddStudentModal, showDeleteModal]);

  const CheckboxOption = (props) => (
    <components.Option {...props}>
      <input
        type="checkbox"
        className="form-check-input me-2"
        checked={props.isSelected}
        onChange={() => null}
      />
      <span>{props.label}</span>
    </components.Option>
  );

  const confirmDelete = async () => {
    if (!selectedBatch?.id) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${LMS}/api/batch/${selectedBatch.id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': token
        }
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && (result.status !== false)) {
        setShowDeleteModal(false);
        setFilterMessage({
          type: 'success',
          text: `Batch "${selectedBatch.batch_name}" deleted successfully.`
        });
        setSelectedBatch(null);
        fetchBatches(pagination.current_page);
      } else {
        setFilterMessage({
          type: 'danger',
          text: result.message || 'Failed to delete batch. Please try again.'
        });
      }
    } catch (error) {
      setFilterMessage({
        type: 'danger',
        text: 'Failed to delete batch. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchBatches(page);
    }
  };

  const formatTime = (t) => {
    const [h, m] = t.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour.toString().padStart(2, '0')}:${m} ${ampm}`;
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });

  const statusBadge = (s) => {
    const status = s?.toLowerCase();
    switch (status) {
      case 'active': return 'bg-success';
      case 'inactive': return 'bg-warning text-dark';
      default: return 'bg-secondary';
    }
  };

  const uniqueStatuses = ['Active', 'Inactive'];

  const renderPagination = () => {
    if (pagination.last_page <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <nav aria-label="Batch pagination">
        <ul className="pagination justify-content-center mb-0">
          <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
            >
              Previous
            </button>
          </li>
          
          {startPage > 1 && (
            <>
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
              </li>
              {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
            </>
          )}

          {pages.map(page => (
            <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(page)}>
                {page}
              </button>
            </li>
          ))}

          {endPage < pagination.last_page && (
            <>
              {endPage < pagination.last_page - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
              <li className="page-item">
                <button className="page-link" onClick={() => handlePageChange(pagination.last_page)}>
                  {pagination.last_page}
                </button>
              </li>
            </>
          )}

          <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div>
      <div className="col-12">
        <PageHeader
          title="Institute Batches"
          breadcrumbs={[
            { label: 'Dashboard', path: '/institute-dashboard' },
            { label: 'Batches', active: true },
          ]}
        />
      </div>

      <div className={isMobile ? 'border-0 mb-4 rounded-2 mt-3' : 'mb-4 rounded-2 p-md-3 border'}>
        <div className="row mb-1 d-none d-md-flex">
          <div className="col-9">
            <label className="form-label text-lg fw-semibold text-dark">Filter Options</label>
          </div>
          <div className="col-3 d-flex justify-content-end">
            <button className="btn btn-primary" onClick={handleAddBatch} disabled={isLoading}>
              <i className="fas fa-plus me-2" /> Add Batch
            </button>
          </div>
        </div>

        <div className="row mb-md-2">
          <div className="col-12">
            <div className="d-md-none">
              <div className="d-grid gap-2">
                <button className="btn btn-primary" onClick={handleAddBatch} disabled={isLoading}>
                  <i className="fas fa-plus me-2" /> Add New Batch
                </button>
                <button className="btn btn-outline-primary" onClick={() => setShowMobileFilters(true)}>
                  <i className="fas fa-filter me-2" /> Filters
                  {(batchNameFilter || programFilter || statusFilter) && (
                    <span className="badge bg-danger ms-2">
                      {[batchNameFilter, programFilter, statusFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="d-none d-md-block">
              <div className="card-body">
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Batch Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search batch..."
                      value={batchNameFilter}
                      onChange={(e) => setBatchNameFilter(e.target.value)}
                    />
                    {isLoading && batchNameFilter && (
                      <small className="text-muted">
                        <i className="fas fa-circle-notch fa-spin me-1" />
                        Searching...
                      </small>
                    )}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Program</label>
                    <select 
                      className="form-select" 
                      value={programFilter} 
                      onChange={(e) => setProgramFilter(e.target.value)}
                    >
                      <option value="">All Programs</option>
                      {programs.map((p) => (
                        <option key={p.id} value={p.program_title}>
                          {p.program_title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-semibold text-dark">Status</label>
                    <select 
                      className="form-select" 
                      value={statusFilter} 
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      {uniqueStatuses.map((s, i) => (
                        <option key={i} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2 d-flex">
                    <button 
                      className="btn btn-outline-secondary " 
                      onClick={handleReset} 
                      disabled={isLoading}
                      title="Reset Filters"
                    >
                      <i className="fas fa-sync-alt" /> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMobileFilters && (
        <>
          <div 
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none" 
            style={{ zIndex: 1040 }} 
            onClick={() => setShowMobileFilters(false)} 
          />
          <div
            className="position-fixed top-0 end-0 h-100 bg-white shadow-lg d-md-none"
            style={{
              width: '320px',
              zIndex: 1050,
              transform: showMobileFilters ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.3s ease-in-out',
              overflowY: 'auto',
            }}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-primary text-white">
              <h5 className="mb-0 text-white">
                <i className="fas fa-filter me-2" /> Filters
              </h5>
              <button 
                className="btn btn-sm btn-outline-light" 
                onClick={() => setShowMobileFilters(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="p-3">
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Batch Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Search batch..." 
                  value={batchNameFilter} 
                  onChange={(e) => setBatchNameFilter(e.target.value)} 
                />
                {isLoading && batchNameFilter && (
                  <small className="text-muted">
                    <i className="fas fa-circle-notch fa-spin me-1" />
                    Searching...
                  </small>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Program</label>
                <select 
                  className="form-select" 
                  value={programFilter} 
                  onChange={(e) => setProgramFilter(e.target.value)}
                >
                  <option value="">All Programs</option>
                  {programs.map((p) => (
                    <option key={p.id} value={p.program_title}>
                      {p.program_title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">Status</label>
                <select 
                  className="form-select" 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  {uniqueStatuses.map((s, i) => (
                    <option key={i} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-3 border-top mt-auto">
              <div className="d-grid gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    handleReset();
                    setShowMobileFilters(false);
                  }}
                  disabled={isLoading}
                >
                  <i className="fas fa-sync-alt me-2" /> Reset All Filters
                </button>
                <button 
                  className="btn btn-success" 
                  onClick={() => setShowMobileFilters(false)}
                >
                  <i className="fas fa-check me-2" /> Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col-12">
          <div className="card skyblueborder">
            <div className="card-header skyblue-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                <i className="fas fa-users me-2" /> Batch List
              </h5>
            </div>
            <div className="card-body p-0">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading batches...</p>
                </div>
              ) : batches.length > 0 ? (
                <>
                  {/* Desktop Table View */}
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0 fw-semibold py-3 px-4">Batch Info</th>
                          <th className="border-0 fw-semibold py-3 px-4">Program</th>
                          <th className="border-0 fw-semibold py-3 px-4 text-center">Students</th>
                          <th className="border-0 fw-semibold py-3 px-4">Duration</th>
                          <th className="border-0 fw-semibold py-3 px-4">Type</th>
                          <th className="border-0 fw-semibold py-3 px-4">Days</th>
                          <th className="border-0 fw-semibold py-3 px-4">Session Time</th>
                          <th className="border-0 fw-semibold py-3 px-4">Status</th>
                          <th className="border-0 fw-semibold py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map((batch) => (
                          <tr key={batch.id}>
                            <td className="py-3 px-4">
                              <div className="fw-semibold text-dark">{batch.batch_name}</div>
                              <small className="text-muted">{batch.teacher_name}</small>
                            </td>
                            <td className="py-3 px-4 text-dark">{batch.program_title}</td>
                            <td className="py-3 px-4 text-center">
                              <span className="fw-semibold">{batch.student_count}</span>
                            </td>
                            <td className="py-3 px-4">
                              <small
                                className="pe-auto"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={`${formatDate(batch.start_date)} - ${formatDate(batch.end_date)}`}
                              >
                                {getDurationLabel(batch.start_date, batch.end_date)}
                              </small>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-capitalize">{batch.batch_type}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="fw-semibold">{batch.days}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="fw-semibold">{formatTime(batch.time)}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`badge ${statusBadge(batch.status)}`}>
                                {batch.status}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => handleView(batch)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="View Batch"
                                >
                                  <i className="fas fa-eye" />
                                </button>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleAddStudent(batch)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Add Student"
                                >
                                  <i className="fas fa-user-plus" />
                                </button>
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleEdit(batch)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Edit Batch"
                                >
                                  <i className="fas fa-edit" />
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(batch)}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Delete Batch"
                                >
                                  <i className="fas fa-trash" />
                                </button>
                                <button
                                  className="btn btn-sm bg-success text-white"
                                  onClick={() => window.location.href = `/batch-report?batch=${batch.id}`}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title="Batch Report"
                                >
                                  <i className="fas fa-file-alt" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile Card View */}
                  <div className="d-md-none p-3">
                    {batches.map((batch) => (
                      <div key={batch.id} className="card mb-3 shadow-sm border">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="fw-bold text-dark mb-1">{batch.batch_name}</h6>
                              <small className="text-muted">Teacher : {batch.teacher_name}</small>
                            </div>
                            <span className={`badge ${statusBadge(batch.status)}`}>
                              {batch.status}
                            </span>
                          </div>
                          
                          <div className="row mb-2">
                            <div className="col-6">
                              <small className="text-muted d-block">Program</small>
                              <span className="fw-semibold">{batch.program_title}</span>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Students</small>
                              <span className="fw-semibold">{batch.student_count}</span>
                            </div>
                          </div>
                          
                          <div className="row mb-2">
                            <div className="col-6">
                              <small className="text-muted d-block">Type</small>
                              <span className="text-capitalize">{batch.batch_type}</span>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Days</small>
                              <span className="fw-semibold">{batch.days}</span>
                            </div>
                          </div>
                          
                          <div className="row mb-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Duration</small>
                              <small
                                className="pe-auto"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={`${formatDate(batch.start_date)} - ${formatDate(batch.end_date)}`}
                              >
                                {getDurationLabel(batch.start_date, batch.end_date)}
                              </small>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Time</small>
                              <span className="fw-semibold">{formatTime(batch.time)}</span>
                            </div>
                          </div>
                          
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => handleView(batch)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="View Batch"
                            >
                              <i className="fas fa-eye" /> View Batch
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleAddStudent(batch)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Add Student"
                            >
                              <i className="fas fa-user-plus" /> Add Student
                            </button>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleEdit(batch)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Edit Batch"
                            >
                              <i className="fas fa-edit" /> Edit Batch
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(batch)}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Delete Batch"
                            >
                              <i className="fas fa-trash" /> Delete Batch
                            </button>
                            <button
                              className="btn btn-sm bg-success text-white"
                              onClick={() => window.location.href = `/batch-report?batch=${batch.id}`}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title="Batch Report"
                            >
                              <i className="fas fa-file-alt" /> Batch Report
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {pagination.total > pagination.per_page && (
                    <div className="card-footer bg-white border-top py-3">
                      <div className="row align-items-center">
                        <div className="col-md-6 mb-2 mb-md-0">
                          <span className="text-muted">
                            Showing {pagination.from} to {pagination.to} of {pagination.total} batches
                          </span>
                        </div>
                        <div className="col-md-6">
                          {renderPagination()}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-users fa-3x text-muted mb-3" />
                  <h6 className="text-muted">No Batches Found</h6>
                  <p className="text-muted mb-3">
                    {(batchNameFilter || programFilter || statusFilter)
                      ? 'No batches match your current filter criteria.'
                      : 'No batches have been created yet.'}
                  </p>
                  {(batchNameFilter || programFilter || statusFilter) ? (
                    <button className="btn btn-outline-primary me-2" onClick={handleReset}>
                      <i className="fas fa-sync-alt me-1" /> Clear Filters
                    </button>
                  ) : null}
                  <button className="btn btn-primary" onClick={handleAddBatch}>
                    <i className="fas fa-plus me-1" /> Add Batch
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showViewModal && selectedBatch && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header skyblue-header pb-3">
                <div>
                <h4 className="mb-0 text-dark">{selectedBatch.batch_name}</h4>
                <small className="text-dark">{selectedBatch.program_title}</small>
                </div>
                <button 
                  type="button" 
                  className="btn-close btn-close-primary" 
                  onClick={() => setShowViewModal(false)} 
                />
              </div>
              <div className="modal-body p-0">
                <div className="card border-0">
                  {/* <div className="card-header skyblue-header">
                    <div>
                      <h4 className="mb-0 text-dark">{selectedBatch.batch_name}</h4>
                      <small className="text-dark">{selectedBatch.program_title}</small>
                    </div>
                  </div> */}
                  <div className="card-body">
                    <div className="row row-cols-1 row-cols-md-3 g-3 mb-2">
                      <div className="col">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-info-circle text-primary me-2" />
                          <strong>Status</strong>
                        </div>
                        <span className={`badge ${statusBadge(selectedBatch.status)}`}>{selectedBatch.status}</span>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-tag text-primary me-2" />
                          <strong>Type</strong>
                        </div>
                        <div className="text-capitalize">{selectedBatch.batch_type}</div>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-users text-primary me-2" />
                          <strong>Capacity</strong>
                        </div>
                        <div>{selectedBatch.student_count} / {selectedBatch.capacity}</div>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-user text-primary me-2" />
                          <strong>Teacher</strong>
                        </div>
                        <div className="mb-0">{selectedBatch.teacher_email}</div>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-calendar-day text-primary me-2" />
                          <strong>Schedule</strong>
                        </div>
                        <div>{selectedBatch.days} <small className="text-muted">({formatTime(selectedBatch.time)})</small></div>
                      </div>
                      <div className="col">
                        <div className="d-flex align-items-center mb-1">
                          <i className="fas fa-clock text-primary me-2" />
                          <strong>Duration</strong>
                        </div>
                        <div>{formatDate(selectedBatch.start_date)} - {formatDate(selectedBatch.end_date)}</div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="border-start border-primary ps-3">
                        <div className="fw-semibold text-primary mb-2">Program Overview</div>
                        <p className="text-dark mb-0">{selectedBatch.description || '—'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer bg-transparent text-end">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowViewModal(false)}
                    >
                      Back to Batches
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddStudentModal && selectedBatch && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white pb-3">
                <h5 className="modal-title text-white">
                  <i className="fas fa-user-plus me-2"></i>Add Students to {selectedBatch.batch_name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => {
                    setShowAddStudentModal(false);
                    setSelectedStudents([]);
                    setDropdownOpen(false);
                    setSelectedBatch(null);
                  }}
                  disabled={isLoading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="card bg-light">
                      <div className="card-body p-3">
                        <h6 className="card-title text-primary mb-2">
                          <i className="fas fa-info-circle me-1"></i> Batch Information
                        </h6>
                        <div className="row">
                          <div className="col-md-6">
                            <p className="mb-1"><strong>Batch:</strong> {selectedBatch.batch_name}</p>
                            <p className="mb-0"><strong>Program:</strong> {selectedBatch.program_title}</p>
                          </div>
                          <div className="col-md-6">
                            <p className="mb-1"><strong>Type:</strong> <span className="text-capitalize">{selectedBatch.batch_type}</span></p>
                            <p className="mb-0"><strong>Current Students:</strong> {selectedBatch.student_count}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">Select Students</label>
                  <Select
                    isMulti
                    isSearchable
                    isClearable
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    controlShouldRenderValue={false}
                    components={{ Option: CheckboxOption }}
                    options={availableStudents.map((s) => ({
                      value: s.id,
                      label: `${s.first_name} ${s.last_name} (${s.email})`,
                      data: s
                    }))}
                    value={selectedStudents.map((s) => ({
                      value: s.id,
                      label: `${s.first_name} ${s.last_name} (${s.email})`,
                      data: s
                    }))}
                    onChange={(vals) => {
                      const list = Array.isArray(vals) ? vals.map(v => v.data) : [];
                      setSelectedStudents(list);
                    }}
                    placeholder="Select students to add to batch..."
                    isDisabled={isLoading}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
                  />
                </div>

                {/* Selected Students Display */}
                {selectedStudents.length > 0 && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Selected Students ({selectedStudents.length})</label>
                    <div className="border rounded p-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                      {selectedStudents.map(student => (
                        <div key={student.id} className="d-flex align-items-center justify-content-between mb-2 p-2 bg-light rounded">
                          <div>
                            <div className="fw-semibold">{student.first_name} {student.last_name}</div>
                            <small className="text-muted d-block">{student.email}</small>
                            <small className="text-muted">{student.phone}</small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeSelectedStudent(student.id)}
                            disabled={isLoading}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddStudentModal(false);
                    setSelectedStudents([]);
                    setDropdownOpen(false);
                    setSelectedBatch(null);
                  }}
                  disabled={isLoading}
                >
                  <i className="fas fa-times me-1"></i> Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveStudent}
                  disabled={selectedStudents.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Adding...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i> Add Students ({selectedStudents.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && selectedBatch && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger pb-3">
                <h5 className="modal-title text-white ">
                  <i className="fas fa-exclamation-triangle me-2" /> Confirm Delete
                </h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={() => setShowDeleteModal(false)} 
                />
              </div>
              <div className="modal-body text-center">
                <i className="fas fa-trash-alt fa-3x text-danger mb-3" />
                <p className="fs-5">Are you sure you want to delete this batch?</p>
                <div className="card bg-light mt-3">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-6 text-start">
                        <strong>Batch:</strong> {selectedBatch.batch_name}
                        <br />
                        <strong>Program:</strong> {selectedBatch.program_title}
                      </div>
                      <div className="col-6 text-start">
                        <strong>Type:</strong> <span className="text-capitalize">{selectedBatch.batch_type}</span>
                        <br />
                        <strong>Students:</strong> {selectedBatch.student_count}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="alert alert-warning mt-3">This action cannot be undone.</div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteModal(false)} 
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={confirmDelete} 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-1" /> Delete Batch
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstituteBatchList;
